import type { NeighborElevationCollection } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import type { DirectionDxDy } from "../../../../core/direction/DirectionDxDy.type"
import getDirectionFromDxDy from "../../../../core/direction/getDirectionFromDxDy"
import getNextEightDirection from "../../../../core/direction/getNextEightDirection"
import normalizeDxDyValue1 from "../../../../core/direction/normalizeDxDyValue1"
import type { EntityGraphNode } from "../../../../core/engine/EntityGraphNode/EntityGraphNode.type"
import type { DeadwoodEntity } from "../../../../core/entity/EntityType/Deadwood"
import type { ForestEntity } from "../../../../core/entity/EntityType/Forest"
import type { JungleEntity } from "../../../../core/entity/EntityType/Jungle"
import type { SwampEntity } from "../../../../core/entity/EntityType/Swamp"
import { noise2D100 } from "../../../../core/rng/SimplexNoise/Global/constNoise2Ds"
import { next, nextIntRange, nextNumberRange } from "../../../../core/rng/index"
import resetEntityRngConfig from "../../../../core/rng/resetEntityRngConfig"
import tmpRng from "../../../../core/rng/tmpRng"
import { HALF_VERTICES_PER_TILE, VERTICES_PER_TILE } from "../../../consts"
import getTerrainMesh1Z from "../../TerrainMesh1V2/getTerrainMesh1Z"

const TREES_PER_ROW = 9

const entJitter = (VERTICES_PER_TILE / TREES_PER_ROW) / 10
const centerDiffThreshold = HALF_VERTICES_PER_TILE / 8
const roundingRadiusSquared = (VERTICES_PER_TILE / 2.5) ** 2

// Curl noise parameters for natural grass distribution
/** Epsilon value for numerical derivative calculation in curl noise */
const eps = 0.02
/** Multiplier for curl noise effect strength */
const curlMult = 1 / 6

type TreePosition = {
  x: number
  y: number
  r: number
  v: number
  h: number
}

type EntityWithTree = EntityGraphNode<ForestEntity>
  | EntityGraphNode<JungleEntity>
  | EntityGraphNode<SwampEntity>
  | EntityGraphNode<DeadwoodEntity>

export default function generateTreePositionsV3(
  list: Array<EntityWithTree>,
  variantsCount: number,
  neighborBaseElevationReactiveMap: Map<number, NeighborElevationCollection>,
): Array<TreePosition> {
  // console.time('generateTreePositionsV3')

  const positions: Array<TreePosition> = []

  let slotX: number
  let slotY: number
  // Curl noise calculation for natural grass distribution
  /** X-component of curl noise vector */
  let curl0: number = 0
  /** Y-component of curl noise vector */
  let curl1: number = 0

  /**
   * Calculates curl noise at the given world coordinates.
   * 
   * Curl noise creates swirling, natural-looking distribution patterns
   * by computing the curl (rotation) of a 2D noise field. This prevents
   * grass from appearing in straight lines or regular patterns.
   * 
   * @param x - World X coordinate
   * @param y - World Y coordinate
   */
  function calculateCurlNoise(x: number, y: number) {
    // Sample noise at four nearby points to compute numerical derivatives
    const n1 = noise2D100(x, y + eps)
    const n2 = noise2D100(x, y - eps)
    const n3 = noise2D100(x + eps, y)
    const n4 = noise2D100(x - eps, y)

    // Compute curl components using central difference
    curl0 = (n1 - n2) / (2 * eps)  // ∂noise/∂y
    curl1 = (n3 - n4) / (2 * eps)  // ∂noise/∂x
  }

  const spaceBetweenTrees = VERTICES_PER_TILE / (TREES_PER_ROW)
  const halfSpaceBetweenTrees = spaceBetweenTrees / 2

  function getForestNeighbor(ent: EntityWithTree, dir: DirectionDxDy) {
    const tile0 = ent.tile
    const neighbor = list.find(ent1 => {
      const tile1 = ent1.tile
      return tile1.x === tile0.x + dir.dx && tile1.y === tile0.y + dir.dy
    })
    return neighbor
  }

  for (let i = 0; i < list.length; i++) {
    const ent = list[i]
    const nodeIndex = ent.tile.index
    const baseElevationsCollection = neighborBaseElevationReactiveMap.get(nodeIndex)
    if (baseElevationsCollection) {
      resetEntityRngConfig(ent.id, tmpRng)

      const tile = ent.tile

      const center0x = tile.x * VERTICES_PER_TILE
      const center0y = tile.y * VERTICES_PER_TILE
      const minX = center0x - HALF_VERTICES_PER_TILE
      const minY = center0y - HALF_VERTICES_PER_TILE
      const maxX = center0x + HALF_VERTICES_PER_TILE
      const maxY = center0y + HALF_VERTICES_PER_TILE

      // const isLeftNeighbor = list.find(ent1 => ent1.y === ent.y && ent1.x + 1 === ent.x)
      // const isRightNeighbor = list.find(ent1 => ent1.y === ent.y && ent1.x - 1 === ent.x)
      // const isTopNeighbor = list.find(ent1 => ent1.x === ent.x && ent1.y - 1 === ent.y)
      // const isBottomNeighbor = list.find(ent1 => ent1.x === ent.x && ent1.y + 1 === ent.y)

      for (let i = minX + halfSpaceBetweenTrees; i < maxX; i += spaceBetweenTrees) {
        for (let j = minY + halfSpaceBetweenTrees; j < maxY; j += spaceBetweenTrees) {
          if (next(tmpRng) <= 0.85) {
            slotX = i
            slotY = j
            calculateCurlNoise(slotX, slotY)
            const wx = slotX + (curl0 * curlMult) + nextNumberRange(-entJitter, entJitter, tmpRng)
            const wy = slotY + (curl1 * curlMult) + nextNumberRange(-entJitter, entJitter, tmpRng)

            const centerXDiff = center0x - wx
            const centerYDiff = center0y - wy
            const absCenterXDiff = Math.abs(centerXDiff)
            const absCenterYDiff = Math.abs(centerYDiff)

            const tx = wx - center0x
            const ty = wy - center0y

            if (absCenterXDiff > centerDiffThreshold || absCenterYDiff > centerDiffThreshold) {
              const distanceFromCenterSquared = centerXDiff * centerXDiff + centerYDiff * centerYDiff
              // avoid, hypot and sqrt are slow
              // const distanceFromCenter = hypot(center0x - wx, center0y - wy)
              if ((nextNumberRange(0, VERTICES_PER_TILE, tmpRng) ** 2) < distanceFromCenterSquared) {
                // randomly skip some edge trees to keep it rounded
                continue
              }

              if (distanceFromCenterSquared > roundingRadiusSquared) {
                const dir1 = getDirectionFromDxDy(
                  normalizeDxDyValue1(tx),
                  normalizeDxDyValue1(ty))

                if (!getForestNeighbor(ent, dir1)) {
                  const dir0 = getNextEightDirection(dir1, -1)
                  if (!getForestNeighbor(ent, dir0)) {
                    const dir2 = getNextEightDirection(dir1, 1)
                    if (!getForestNeighbor(ent, dir2)) {
                      // this is a corner, but none of the neighbors are trees,
                      // so we should avoid a tree to keep it rounded
                      continue
                    }
                  }
                }
              }
            }

            const r = nextNumberRange(0, Math.PI * 2, tmpRng)
            const v = nextIntRange(0, variantsCount - 1, tmpRng)
            const h = getTerrainMesh1Z(baseElevationsCollection, tx, -ty)
            // const h = 1

            positions.push({ x: wx, y: wy, r, v, h })
          }
        }
      }
    }
  }

  // console.timeEnd('generateTreePositionsV3')

  return positions
}